/**
 * The sign-in code box's rules (ACOP-164, SEC-20). Free of React so they can be unit-tested directly.
 *
 * Salesforce issues a 4-digit code by default and a 6-digit one when the request asks for it. This build can type
 * either, so the box takes 4 to 6 digits and every request for a code asks for 6. A screen left open from before
 * this change never runs this code: it sends no request, gets 4 digits, and its own 4-digit box still works.
 */

/** The fewest digits a code can have (the 4-digit default, and the dev/stgn test PIN). */
export const CODE_MIN_DIGITS = 4;
/** The most digits a code can have. */
export const CODE_MAX_DIGITS = 6;
/** The length this screen asks Salesforce for. Only the number 6 is ever passed on by the server. */
export const REQUESTED_CODE_DIGITS = 6;

/**
 * What the box holds for whatever was typed or pasted: ASCII digits only, at most CODE_MAX_DIGITS. The cap comes
 * AFTER the clean-up, so a pasted `123 456` or `123-456` keeps all six digits (the input has no `maxlength`, which
 * would cut the paste before the space could be stripped).
 */
export function cleanCodeInput(raw: unknown): string {
  if (raw === undefined || raw === null) return "";
  return String(raw).replace(/[^0-9]/g, "").slice(0, CODE_MAX_DIGITS);
}

/** True for a string of CODE_MIN_DIGITS to CODE_MAX_DIGITS ASCII digits: a code worth sending. */
export function isCompleteCode(code: unknown): boolean {
  return typeof code === "string" && /^[0-9]+$/.test(code) && code.length >= CODE_MIN_DIGITS && code.length <= CODE_MAX_DIGITS;
}

/**
 * Add the request for a 6-digit code to a code/send body (a JSON string, as callApiPost posts it). This screen
 * decides the length, so any `codeLength` already in the body is replaced. A body that is not a JSON object is
 * returned as it was.
 */
export function withCodeLengthRequest(body: string): string {
  let parsed: unknown = {};
  if (typeof body === "string" && body !== "") {
    try {
      parsed = JSON.parse(body);
    } catch {
      return body;
    }
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return body;
  return JSON.stringify({ ...(parsed as Record<string, unknown>), codeLength: REQUESTED_CODE_DIGITS });
}
